// frontend/src/pages/master/CategoryMaster.jsx

import React, { useState, useEffect, useContext } from 'react';
import { Link } from 'react-router-dom';
import Select from 'react-select';
import { apiFetch } from '../../utils/api';
import { AppSettings } from '../../config/app-settings.js';

const CategoryMaster = () => {
  const context = useContext(AppSettings);
  const user_data = context.userInfo;
  const user = user_data ? user_data.user_info : null;
  const authorities = user_data ? user_data.authorities : null;

  const [categories, setCategories] = useState([]);
  const [variantTypes, setVariantTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedNodes, setExpandedNodes] = useState(new Set());

  const [selectedL1, setSelectedL1] = useState(null);
  const [selectedL2, setSelectedL2] = useState(null);
  const [editingUuid, setEditingUuid] = useState(null);

  const [hasPermission, setHasPermission] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    note: '',
    variant_1: { variant_type_uuid: '' },
    variant_2: { variant_type_uuid: '' }
  });

  useEffect(() => {
    fetchCategories();
    fetchVariantTypes();
  }, []);

  useEffect(() => {
    const userRole = user?.role || user?.auth_class || authorities?.[0]?.role || 'guest';
    const allowedRoles = ['admin', 'director', 'manager'];
    setHasPermission(allowedRoles.includes(userRole));
  }, [user_data]);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const res = await apiFetch('/categories');
      if (res.ok) setCategories(await res.json());
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchVariantTypes = async () => {
    try {
      const res = await apiFetch('/variant-options/types');
      if (res.ok) setVariantTypes(await res.json());
    } catch (error) {
      console.error(error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleResetForm = () => {
    setEditingUuid(null);
    setFormData({
      name: '',
      code: '',
      note: '',
      variant_1: { variant_type_uuid: '' },
      variant_2: { variant_type_uuid: '' }
    });
  };

  const handleL1Change = (option) => {
    setSelectedL1(option);
    setSelectedL2(null);
    handleResetForm();
  };

  const handleL2Change = (option) => {
    setSelectedL2(option);
    handleResetForm();
  };

  // 🌟 改善：ツリーからジャンルを選んだ際、すでに選択中のものをもう一度クリックしたら解除するトグル機能を搭載
  const handleSelectFromTree = (cat) => {

    // 💡 すでに編集中（選択中）のカテゴリをもう一度クリックした場合は、編集モードを解除して新規追加に戻る
    if (editingUuid === cat.uuid) {
      handleResetForm();
      return;
    }

    setEditingUuid(cat.uuid); // 編集対象のUUIDをロック！

    if (cat.level === 1) {
      setSelectedL1(null);
      setSelectedL2(null);
      setFormData({ name: cat.name, code: cat.code, note: cat.note || '', variant_1: { variant_type_uuid: '' }, variant_2: { variant_type_uuid: '' } });
    } else if (cat.level === 2) {
      const parentL1 = categories.find(c => c.uuid === cat.parent_uuid);
      if (parentL1) setSelectedL1({ value: parentL1.uuid, label: `【大分類】${parentL1.name} (${parentL1.code})` });
      setSelectedL2(null);
      setFormData({ name: cat.name, code: cat.code, note: cat.note || '', variant_1: { variant_type_uuid: '' }, variant_2: { variant_type_uuid: '' } });
    } else if (cat.level === 3) {
      const parentL2 = categories.find(c => c.uuid === cat.parent_uuid);
      if (parentL2) {
        const parentL1 = categories.find(c => c.uuid === parentL2.parent_uuid);
        if (parentL1) setSelectedL1({ value: parentL1.uuid, label: `【大分類】${parentL1.name} (${parentL1.code})` });
        setSelectedL2({ value: parentL2.uuid, label: `【中分類】${parentL2.name} (${parentL2.code})` });
      }

      const v1 = cat.variants?.find(v => v.display_order === 1) || { variant_type_uuid: '' };
      const v2 = cat.variants?.find(v => v.display_order === 2) || { variant_type_uuid: '' };

      setFormData({
        name: cat.name,
        code: cat.code,
        note: cat.note || '',
        variant_1: { variant_type_uuid: v1.variant_type_uuid || '' },
        variant_2: { variant_type_uuid: v2.variant_type_uuid || '' }
      });
    }
  };

  const toggleNode = (uuid, e) => {
    e.stopPropagation();
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(uuid)) next.delete(uuid);
      else next.add(uuid);
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!hasPermission) return alert("エラー: あなたの権限ではこの操作は許可されていません。");

    if (formData.code === '00') {
      alert("エラー: コード '00' はシステム予約値のため、カテゴリコードとして登録できません。");
      return;
    }

    const finalParentUuid = selectedL2 ? selectedL2.value : (selectedL1 ? selectedL1.value : null);

    const variantsPayload = [];
    if (formData.variant_1.variant_type_uuid) {
      variantsPayload.push({ variant_type_uuid: formData.variant_1.variant_type_uuid, display_order: 1 });
    }
    if (formData.variant_2.variant_type_uuid) {
      variantsPayload.push({ variant_type_uuid: formData.variant_2.variant_type_uuid, display_order: 2 });
    }

    const payload = {
      name: formData.name,
      code: formData.code,
      note: formData.note,
      parent_uuid: finalParentUuid,
      variants: variantsPayload
    };

    try {
      const url = editingUuid ? `/categories/${editingUuid}` : '/categories';
      const method = editingUuid ? 'PUT' : 'POST';

      const res = await apiFetch(url, {
        method: method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.detail || '保存に失敗しました');
      }

      alert(editingUuid ? '自社ジャンルを更新（上書き）しました！' : '自社ジャンルを新規登録しました！');
      handleResetForm();
      fetchCategories();

      if (finalParentUuid) {
        setExpandedNodes(prev => new Set(prev).add(finalParentUuid));
      }
    } catch (error) {
      alert(error.message);
    }
  };

  const handleDelete = async (cat, e) => {
    e.stopPropagation();
    if (!hasPermission) return alert("エラー: あなたの権限では削除操作は許可されていません。");

    let confirmMsg = 'このカテゴリを削除しますか？';
    if (cat.level === 1) {
      confirmMsg = `大分類「${cat.name}」を削除します。\nこれに紐づく【すべての中分類・小分類】も一緒に一括削除されます。\n本当によろしいですか？`;
    } else if (cat.level === 2) {
      confirmMsg = `中分類「${cat.name}」を削除します。\nこれに紐づく【すべての小分類】も一緒に一括削除されます。\n本当によろしいですか？`;
    } else {
      confirmMsg = `小分類「${cat.name}」を削除します。よろしいですか？`;
    }

    if (!window.confirm(confirmMsg)) return;

    try {
      const res = await apiFetch(`/categories/${cat.uuid}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('削除に失敗しました');

      if (selectedL1?.value === cat.uuid || editingUuid === cat.uuid) { handleResetForm(); setSelectedL1(null); setSelectedL2(null); }
      if (selectedL2?.value === cat.uuid) { setSelectedL2(null); }

      fetchCategories();
    } catch (error) {
      alert(error.message);
    }
  };

  const l1Options = categories.filter(c => c.level === 1).sort((a, b) => a.code.localeCompare(b.code)).map(c => ({ value: c.uuid, label: `【大分類】${c.name} (${c.code})` }));
  const l2Options = categories.filter(c => c.level === 2 && c.parent_uuid === selectedL1?.value).sort((a, b) => a.code.localeCompare(b.code)).map(c => ({ value: c.uuid, label: `【中分類】${c.name} (${c.code})` }));

  const isCreatingLevel3 = !!selectedL2 || (editingUuid && categories.find(c => c.uuid === editingUuid)?.level === 3);

  const selectStyles = {
    control: (base) => ({ ...base, minHeight: '38px', borderColor: '#dee2e6', boxShadow: 'none', '&:hover': { borderColor: '#b1bbc4' }, cursor: hasPermission ? 'pointer' : 'not-allowed' })
  };

  const variantMasterOptions = [
    { value: '', label: 'なし (使用しない)', variant_type_uuid: '' },
    ...variantTypes.map(type => ({
      value: type.uuid,
      label: `${type.label} (${type.option_type})`,
      variant_type_uuid: type.uuid
    }))
  ];

  const getVariantOption = (variantTypeUuid) => {
    if (!variantTypeUuid) return variantMasterOptions[0];
    return variantMasterOptions.find(opt => opt.variant_type_uuid === variantTypeUuid) || { value: variantTypeUuid, label: '不明な属性枠', variant_type_uuid: variantTypeUuid };
  };

  const handleVariantChange = (selectedOption, variantNum) => {
    const uuidVal = selectedOption ? selectedOption.variant_type_uuid : '';
    setFormData(prev => ({
      ...prev,
      [`variant_${variantNum}`]: { variant_type_uuid: uuidVal }
    }));
  };

  const renderTree = () => {
    const l1Cats = categories.filter(c => c.level === 1).sort((a, b) => a.code.localeCompare(b.code));
    if (l1Cats.length === 0) return <div className="p-5 text-center text-muted">まだジャンルが登録されていません。</div>;

    return (
      <div className="custom-tree-container">
        {l1Cats.map(l1 => {
          const l2Cats = categories.filter(c => c.parent_uuid === l1.uuid).sort((a, b) => a.code.localeCompare(b.code));
          const isL1Expanded = expandedNodes.has(l1.uuid);
          const hasChildrenL1 = l2Cats.length > 0;
          const isL1Editing = editingUuid === l1.uuid;

          return (
            <div key={l1.uuid} className={`mb-4 p-3 border rounded-3 shadow-sm ${isL1Editing ? 'bg-indigo bg-opacity-10 border-indigo' : 'bg-white'}`}>
              <div className="tree-row d-flex align-items-center justify-content-between p-2 rounded transition-hover" onClick={() => handleSelectFromTree(l1)}>
                <div className="d-flex align-items-center">
                  {hasChildrenL1 ? (
                    <div className="tree-toggle-btn" onClick={(e) => toggleNode(l1.uuid, e)}><i className={`fa fa-chevron-${isL1Expanded ? 'down' : 'right'} text-secondary`}></i></div>
                  ) : (<div className="tree-toggle-spacer"></div>)}
                  <span className="badge bg-dark ms-3 me-3 font-monospace">{l1.code}</span>
                  <span className={`fw-bold fs-15px ${isL1Editing ? 'text-indigo' : 'text-dark'}`}>{l1.name} {isL1Editing && '📝'}</span>
                </div>

                {hasPermission && (
                  <button
                    type="button"
                    className={`btn btn-sm border-0 shadow-none trash-btn ${hasChildrenL1 ? 'btn-light text-muted opacity-25' : 'btn-light text-danger'}`}
                    onClick={(e) => handleDelete(l1, e)}
                    disabled={hasChildrenL1}
                    title={hasChildrenL1 ? "下層に中分類カテゴリが存在するため削除できません" : "大分類を削除"}
                    style={hasChildrenL1 ? { cursor: 'not-allowed', opacity: 0.3 } : {}}
                  >
                    <i className="fa fa-trash"></i>
                  </button>
                )}
              </div>

              {isL1Expanded && l2Cats.map(l2 => {
                const l3Cats = categories.filter(c => c.parent_uuid === l2.uuid).sort((a, b) => a.code.localeCompare(b.code));
                const isL2Expanded = expandedNodes.has(l2.uuid);
                const hasChildrenL2 = l3Cats.length > 0;
                const isL2Editing = editingUuid === l2.uuid;

                return (
                  <div key={l2.uuid} className="ms-4 mt-2 ps-2">
                    <div className={`tree-row d-flex align-items-center justify-content-between p-2 rounded transition-hover ${isL2Editing ? 'bg-indigo bg-opacity-10 text-indigo' : ''}`} onClick={() => handleSelectFromTree(l2)}>
                      <div className="d-flex align-items-center">
                        {hasChildrenL2 ? (
                          <div className="tree-toggle-btn" onClick={(e) => toggleNode(l2.uuid, e)}><i className={`fa fa-chevron-${isL2Expanded ? 'down' : 'right'} text-secondary`}></i></div>
                        ) : (<div className="tree-toggle-spacer"></div>)}
                        <span className="badge bg-indigo ms-3 me-3 font-monospace">{l2.code}</span>
                        <span className={`fw-semibold ${isL2Editing ? 'text-indigo' : 'text-dark'}`}>{l2.name} {isL2Editing && '📝'}</span>
                      </div>

                      {hasPermission && (
                        <button
                          type="button"
                          className={`btn btn-sm border-0 shadow-none trash-btn ${hasChildrenL2 ? 'btn-light text-muted opacity-25' : 'btn-light text-danger'}`}
                          onClick={(e) => handleDelete(l2, e)}
                          disabled={hasChildrenL2}
                          title={hasChildrenL2 ? "下層に小分類カテゴリが存在するため削除できません" : "中分類を削除"}
                          style={hasChildrenL2 ? { cursor: 'not-allowed', opacity: 0.3 } : {}}
                        >
                          <i className="fa fa-trash"></i>
                        </button>
                      )}
                    </div>

                    {isL2Expanded && l3Cats.map(l3 => {
                      const v1Rule = l3.variants?.find(v => v.display_order === 1);
                      const v2Rule = l3.variants?.find(v => v.display_order === 2);
                      const v1Name = variantTypes.find(t => t.uuid === v1Rule?.variant_type_uuid)?.label || 'なし';
                      const v2Name = variantTypes.find(t => t.uuid === v2Rule?.variant_type_uuid)?.label || 'なし';
                      const vText = `${v1Name} × ${v2Name}`;
                      const isL3Editing = editingUuid === l3.uuid;

                      return (
                        <div key={l3.uuid} className="ms-4 mt-1 ps-2">
                          <div className={`tree-row d-flex align-items-center justify-content-between p-2 rounded transition-hover ${isL3Editing ? 'bg-indigo bg-opacity-10 text-indigo fw-bold' : ''}`} onClick={() => handleSelectFromTree(l3)}>
                            <div className="d-flex align-items-center ps-4">
                              <div className="tree-toggle-spacer"></div>
                              <span className="badge bg-info text-dark ms-3 me-3 font-monospace">{l3.code}</span>
                              <span className="small">{l3.name} {isL3Editing && '📝'}</span>
                              <span className="badge bg-white text-muted border ms-3 fw-normal" style={{ fontSize: '0.75rem', padding: '0.2rem 0.5rem' }}>
                                {vText}
                              </span>
                            </div>

                            {hasPermission && (
                              <button type="button" className="btn btn-sm btn-light text-danger border-0 shadow-none trash-btn" onClick={(e) => handleDelete(l3, e)}><i className="fa fa-trash"></i></button>
                            )}
                          </div>
                        </div>
                      )
                    })}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="pb-5">
      <style>{`
        .transition-hover { transition: all 0.15s ease; cursor: pointer; }
        .transition-hover:hover { background-color: #f8f9fa; }
        .tree-toggle-btn { width: 26px; height: 26px; display: flex; align-items: center; justify-content: center; cursor: pointer; border-radius: 6px; border: 1px solid #cbd5e1; background-color: #ffffff; box-shadow: 0 1px 2px rgba(0,0,0,0.04); transition: all 0.2s ease; font-size: 10px; }
        .tree-toggle-btn:hover { background-color: #f1f5f9; border-color: #94a3b8; }
        .tree-toggle-spacer { width: 26px; height: 26px; display: inline-block; border-radius: 6px; border: 1px solid #e2e8f0; background-color: #f8fafc; }
        .trash-btn { opacity: 0; transition: opacity 0.2s; }
        .transition-hover:hover .trash-btn { opacity: 1; }
      `}</style>

      <div className="d-flex align-items-center mb-4">
        <div>
          <ol className="breadcrumb mb-1">
            <li className="breadcrumb-item"><Link to="/">Home</Link></li>
            <li className="breadcrumb-item active">自社ジャンルマスター管理</li>
          </ol>
          <h2 className="page-header mb-0 fw-bold">自社共通ジャンル (大・中・小分類)</h2>
        </div>
      </div>

      <div className="row gx-4">
        <div className="col-lg-5">
          <div className="card border-0 shadow-sm mb-4 sticky-top" style={{ top: '20px', zIndex: 1 }}>
            <div className={`card-header pt-3 pb-2 text-white ${editingUuid ? 'bg-indigo' : 'bg-dark'}`}>
              <h6 className="mb-0 fw-bold">
                {editingUuid ? <><i className="fa fa-edit me-2"></i>選択中のジャンルを編集・上書き</> : <><i className="fa fa-plus-circle me-2"></i>新しいジャンルを追加</>}
              </h6>
            </div>
            <div className="card-body p-4">

              {!hasPermission && (
                <div className="alert alert-warning border-0 shadow-sm mb-4 small fw-bold text-dark d-flex align-items-center">
                  <i className="fa fa-exclamation-triangle fs-4 me-3 text-warning"></i>
                  <div>閲覧専用モード：マスターの追加・変更・削除権限がありません（admin, director, manager 限定機能です）</div>
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label className="form-label fw-bold small text-muted mb-1">所属する大分類（レベル1）</label>
                  <Select options={l1Options} value={selectedL1} onChange={handleL1Change} isClearable placeholder="-- 大分類として新規作成 --" styles={selectStyles} noOptionsMessage={() => "大分類がありません"} isDisabled={!!editingUuid || !hasPermission} />
                </div>
                <div className="mb-3">
                  <label className="form-label fw-bold small text-muted mb-1">所属する中分類（レベル2）</label>
                  <Select options={l2Options} value={selectedL2} onChange={handleL2Change} isClearable isDisabled={!selectedL1 || !!editingUuid || !hasPermission} placeholder={selectedL1 ? "-- 中分類として新規作成 --" : "先に大分類を選択してください"} styles={selectStyles} noOptionsMessage={() => "この大分類の下に中分類がありません"} />
                </div>
                <hr className="my-4 text-muted" />
                <div className="row mb-3">
                  <div className="col-8">
                    <label className="form-label fw-bold small text-muted">ジャンル名 <span className="text-danger">*</span></label>
                    <input type="text" className="form-control" name="name" value={formData.name} onChange={handleChange} placeholder="例: ドレス" required disabled={!hasPermission} />
                  </div>
                  <div className="col-4">
                    <label className="form-label fw-bold small text-muted">2桁コード <span className="text-danger">*</span></label>
                    <input type="text" className="form-control font-monospace text-center fw-bold" name="code" value={formData.code} onChange={handleChange} maxLength="2" placeholder="例: 01" required disabled={!hasPermission} />
                  </div>
                </div>

                {isCreatingLevel3 && (
                  <div className="row g-2 mb-3 p-3 bg-light rounded border-start border-info border-4">
                    <div className="col-12 mb-1">
                      <span className="badge bg-info text-dark mb-2">小分類専用設定</span>
                      <p className="small text-muted mb-2">この小分類に属する商品のバリエーション（選択肢）の仕組みを設定します。</p>
                    </div>
                    <div className="col-6">
                      <label className="form-label small text-muted mb-1">第1属性名</label>
                      <Select
                        options={variantMasterOptions}
                        value={getVariantOption(formData.variant_1.variant_type_uuid)}
                        onChange={(opt) => handleVariantChange(opt, 1)}
                        styles={selectStyles}
                        isDisabled={!hasPermission}
                      />
                    </div>
                    <div className="col-6">
                      <label className="form-label small text-muted mb-1">第2属性名</label>
                      <Select
                        options={variantMasterOptions}
                        value={getVariantOption(formData.variant_2.variant_type_uuid)}
                        onChange={(opt) => handleVariantChange(opt, 2)}
                        styles={selectStyles}
                        isDisabled={!hasPermission}
                      />
                    </div>
                  </div>
                )}

                <div className="d-flex gap-2 mt-3">
                  {editingUuid && (
                    <button type="button" className="btn btn-outline-success fw-bold px-3 text-nowrap" onClick={handleResetForm}>
                      ➕ 新規登録モードに戻る
                    </button>
                  )}
                  {hasPermission && (
                    <button type="submit" className={`btn w-100 fw-bold shadow-sm py-2 ${editingUuid ? 'btn-indigo' : 'btn-success'}`}>
                      <i className="fa fa-save me-2"></i>{editingUuid ? '変更内容を上書き保存' : 'この設定でジャンルを登録'}
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>

        <div className="col-lg-7">
          <div className="card border-0 shadow-sm">
            <div className="card-header bg-white border-bottom pt-4 pb-3">
              <h5 className="mb-0 fw-bold"><i className="fa fa-sitemap text-primary me-2"></i>現在のジャンル階層構造</h5>
            </div>
            <div className="card-body p-4 overflow-auto bg-light bg-opacity-60" style={{ maxHeight: '80vh' }}>
              {loading ? (
                <div className="p-5 text-center text-muted">
                  <i className="fa fa-spinner fa-spin fa-2x mb-3"></i><br />データを読み込み中...
                </div>
              ) : renderTree()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryMaster;